import { WAREHOUSE_ATTRS } from './types.js';
import { normalizeArticle, round2, text } from './values.js';
export function requestKey(no) {
    return text(no).toUpperCase();
}
export function lineKey(reqKeyValue, art) {
    return reqKeyValue + '|' + art;
}
export function lineStatus(demand, bought, overdue) {
    if (demand <= 0)
        return 'Отменено';
    if (bought >= demand)
        return 'Закуплено';
    if (overdue)
        return 'Просрочен';
    if (bought > 0)
        return 'Частично закуплено';
    return 'Ожидает закупки';
}
function isOverdue(deadline, today) {
    return !!deadline && deadline < today;
}
function sortRequests(list) {
    return list.slice().sort((a, b) => (a.created === b.created ? a.no.localeCompare(b.no) : a.created < b.created ? -1 : 1));
}
function newAgg(art) {
    return {
        art,
        name: '',
        demand: 0,
        bought: 0,
        packed: 0,
        stock: null,
        stockDate: null,
        boughtAfterStock: 0,
        pool: 0,
        targeted: {},
        factoryPrice: null,
        salePrice: null,
        suggestion: '',
        hs: '',
        wattrs: {},
        openDemand: 0,
        overdue: false,
        lines: []
    };
}
export function computeEngine(journals, today) {
    const requests = sortRequests(journals.requests);
    const byKey = {};
    requests.forEach((r) => (byKey[requestKey(r.no)] = r));
    const arts = {};
    const agg = (a) => (arts[a] ??= newAgg(a));
    // Потребность по запросам (в порядке FIFO)
    const lines = [];
    const linesByKey = {};
    for (const req of requests) {
        const rk = requestKey(req.no);
        for (const it of req.items) {
            const a = normalizeArticle(it.art);
            const ag = agg(a);
            if (it.name)
                ag.name = it.name;
            if (it.qty > 0) {
                ag.demand += it.qty;
                if (it.price !== null)
                    ag.salePrice = it.price;
                const line = {
                    req,
                    reqKey: rk,
                    item: it,
                    demand: it.qty,
                    bought: 0,
                    toBuy: 0,
                    boughtAmount: null,
                    status: 'Ожидает закупки'
                };
                lines.push(line);
                ag.lines.push(line);
                linesByKey[lineKey(rk, a)] = line;
            }
        }
    }
    // Закупки и наличие у фабрики (в порядке проведения)
    for (const p of journals.purchases) {
        const a = normalizeArticle(p.art);
        if (!a)
            continue;
        const ag = agg(a);
        if (p.type === 'stock' && p.stock !== null) {
            ag.stock = p.stock;
            ag.stockDate = p.date;
            ag.boughtAfterStock = 0;
        }
        if (p.type === 'purchase' && p.bought !== null) {
            ag.bought += p.bought;
            ag.boughtAfterStock += p.bought;
            const rk = requestKey(p.reqNo);
            if (rk)
                ag.targeted[rk] = (ag.targeted[rk] ?? 0) + p.bought;
            else
                ag.pool += p.bought;
        }
        if (p.price !== null)
            ag.factoryPrice = p.price;
        if (text(p.suggestion))
            ag.suggestion = text(p.suggestion);
        if (text(p.hs))
            ag.hs = text(p.hs);
    }
    // Упаковка
    for (const k of journals.packing) {
        const a = normalizeArticle(k.art);
        if (!a)
            continue;
        const ag = agg(a);
        ag.packed += k.qty;
        for (const key of WAREHOUSE_ATTRS) {
            const v = text(k.attrs[key]);
            if (v)
                ag.wattrs[key] = v;
        }
    }
    // Распределение: 1) закреплённые закупки; 2) остальное — FIFO
    for (const ag of Object.values(arts)) {
        let pool = ag.pool;
        const targetedTotal = Object.values(ag.targeted).reduce((s, v) => s + v, 0);
        let targetedUsed = 0;
        for (const l of ag.lines) {
            const t = ag.targeted[l.reqKey] ?? 0;
            l.bought = Math.min(l.demand, t);
            targetedUsed += l.bought;
        }
        // Закреплённое сверх потребности запроса (или за снятой позицией) переходит в общую очередь
        pool += targetedTotal - targetedUsed;
        for (const l of ag.lines) {
            const add = Math.min(l.demand - l.bought, pool);
            l.bought += add;
            pool -= add;
            l.toBuy = l.demand - l.bought;
            l.boughtAmount = l.item.price !== null ? round2(l.bought * l.item.price) : null;
            const overdue = isOverdue(l.req.deadline, today) && l.bought < l.demand;
            l.status = lineStatus(l.demand, l.bought, overdue);
            ag.openDemand += l.demand - l.bought;
            if (overdue)
                ag.overdue = true;
        }
    }
    // Итоги по запросам
    const reqSummary = {};
    for (const req of requests) {
        const rk = requestKey(req.no);
        let positions = 0;
        let qty = 0;
        let bought = 0;
        let amount = 0;
        let packable = false;
        for (const it of req.items) {
            const a = normalizeArticle(it.art);
            const l = linesByKey[lineKey(rk, a)];
            if (!l)
                continue;
            positions++;
            qty += l.demand;
            bought += l.bought;
            const ag = arts[a];
            if (l.bought > 0 && ag && ag.bought > ag.packed)
                packable = true;
            if (it.price !== null)
                amount += l.demand * it.price;
        }
        let status;
        if (positions === 0)
            status = 'Отменено';
        else if (bought >= qty)
            status = 'Выполнен';
        else if (isOverdue(req.deadline, today))
            status = 'Просрочен';
        else if (bought > 0)
            status = 'Частично выполнен';
        else
            status = 'Открыт';
        reqSummary[rk] = { positions, qty, bought, packable, done: qty > 0 ? bought / qty : 0, amount: round2(amount), status };
    }
    return { today, requests, byKey, lines, linesByKey, arts, reqSummary };
}
/** Производные показатели по OEM. */
export function articleMetrics(ag) {
    const toBuy = Math.max(0, ag.demand - ag.bought);
    const free = Math.max(0, ag.bought - ag.demand);
    const stockLeft = ag.stock !== null ? Math.max(0, ag.stock - ag.boughtAfterStock) : null;
    const canBuy = stockLeft !== null ? Math.min(toBuy, stockLeft) : null;
    const margin = ag.salePrice !== null && ag.factoryPrice !== null ? round2(ag.salePrice - ag.factoryPrice) : null;
    const status = free > 0 ? 'Излишек' : lineStatus(ag.demand, ag.bought, ag.overdue);
    return { toBuy, free, stockLeft, canBuy, margin, packable: Math.max(0, ag.bought - ag.packed), status };
}
//# sourceMappingURL=engine.js.map